import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Briefcase, MapPin, Clock, ChevronDown, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Careers() {
    const [vacancies, setVacancies] = useState([]);
    const [open, setOpen] = useState(null);
    const [applying, setApplying] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get("/vacancies").then((r) => setVacancies(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="careers-page">
            <section className="bg-ink text-white py-24 md:py-28">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Careers</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1] tracking-tight max-w-4xl">
                        Build the future<br/>with us.
                    </h1>
                    <p className="text-lg text-white/70 mt-6 max-w-2xl">
                        We hire smart, kind, ambitious people who want to do their best work in a fast-growing East African enterprise.
                    </p>
                </div>
            </section>

            {/* Benefits */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                    { title: "Competitive pay", text: "Above-market salaries + bonuses" },
                    { title: "Growth budgets", text: "Annual learning & certification budget" },
                    { title: "Health cover", text: "Medical insurance for you & dependants" },
                    { title: "Hybrid work", text: "Flexibility where the role allows" },
                ].map((b) => (
                    <div key={b.title} className="border-l-4 border-primary pl-4">
                        <div className="font-display font-bold mb-1">{b.title}</div>
                        <div className="text-sm text-muted-foreground">{b.text}</div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-8 pb-24" data-testid="vacancies-list">
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">Open positions</h2>
                {vacancies.length === 0 && (
                    <div className="bg-surface border border-border p-8 text-center text-muted-foreground">
                        No openings right now. Check back soon!
                    </div>
                )}
                <div className="space-y-3">
                    {vacancies.map((v) => (
                        <div key={v.id} className="border border-border bg-white" data-testid={`vacancy-${v.id}`}>
                            <button
                                onClick={() => setOpen(open === v.id ? null : v.id)}
                                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors"
                            >
                                <div>
                                    <h3 className="font-display font-bold text-lg">{v.title}</h3>
                                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {v.department}</span>
                                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.location}</span>
                                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {v.employment_type}</span>
                                    </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 transition-transform ${open === v.id ? "rotate-180" : ""}`} />
                            </button>
                            {open === v.id && (
                                <div className="px-6 pb-6 border-t border-border pt-4">
                                    <p className="text-muted-foreground mb-4 leading-relaxed">{v.description}</p>
                                    {v.requirements?.length > 0 && (
                                        <>
                                            <div className="font-semibold mb-2">Requirements</div>
                                            <ul className="space-y-1 mb-6">
                                                {v.requirements.map((r, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm">
                                                        <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                                                        <span>{r}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    <button
                                        onClick={() => { setApplying(v); setSubmitted(false); }}
                                        className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold hover:bg-primary-hover transition-colors text-sm"
                                        data-testid={`apply-btn-${v.id}`}
                                    >
                                        Apply for this role
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {applying && (
                <ApplicationModal
                    vacancy={applying}
                    onClose={() => { setApplying(null); setSubmitted(false); }}
                    submitted={submitted}
                    setSubmitted={setSubmitted}
                />
            )}
        </div>
    );
}

function ApplicationModal({ vacancy, onClose, submitted, setSubmitted }) {
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_letter: "" });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        if (!file) {
            toast.error("Please upload your CV");
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("purpose", "cv");
            const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const cv_url = up.data.url;
            await api.post("/applications", {
                vacancy_id: vacancy.id,
                cv_url,
                ...form,
            });
            setSubmitted(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not submit application");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose} data-testid="application-modal">
            <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {submitted ? (
                    <div className="p-10 text-center">
                        <CheckCircle2 className="w-12 h-12 text-secondary mx-auto mb-4" />
                        <h3 className="font-display text-2xl font-bold mb-2">Application received!</h3>
                        <p className="text-muted-foreground mb-6">We'll review and get back to you within 7 working days.</p>
                        <button onClick={onClose} className="bg-primary text-white px-5 py-2.5 font-semibold">Close</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-6">
                        <h3 className="font-display text-2xl font-bold mb-1">Apply: {vacancy.title}</h3>
                        <p className="text-sm text-muted-foreground mb-6">{vacancy.department} · {vacancy.location}</p>
                        <div className="grid sm:grid-cols-2 gap-3 mb-3">
                            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Full name" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="application-name" />
                            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="application-email" />
                            <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className="sm:col-span-2 border border-border px-3 py-2.5 focus:outline-none focus:border-primary" data-testid="application-phone" />
                        </div>
                        <textarea value={form.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} placeholder="Cover letter (optional)" rows={4} className="w-full border border-border px-3 py-2.5 focus:outline-none focus:border-primary mb-3" data-testid="application-cover-letter" />
                        <label className="block">
                            <div className="overline text-muted-foreground mb-2">CV / Resume (PDF) *</div>
                            <div className="border-2 border-dashed border-border p-6 text-center hover:border-primary cursor-pointer transition-colors">
                                <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                                <div className="text-sm text-muted-foreground">{file ? file.name : "Click or drop your CV here"}</div>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="hidden"
                                    data-testid="application-cv-input"
                                />
                            </div>
                        </label>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={onClose} className="flex-1 border border-border px-5 py-2.5 font-semibold">Cancel</button>
                            <button type="submit" disabled={loading} className="flex-1 bg-primary text-white px-5 py-2.5 font-semibold hover:bg-primary-hover disabled:opacity-50" data-testid="application-submit">
                                {loading ? "Submitting…" : "Submit application"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
